import { type FC, type ReactNode, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Alert,
} from "@mui/material";
import { usePayment } from "@/entities/payments";
import { useTranslation } from "@/shared/hooks";
import { FixWithAssistantButton } from "@/features/assistant";
import { resolveTerminalStatus, type TerminalStatus } from "./terminalStatus";

interface PaymentStatusStepProps {
  paymentId: string;
  onClose: () => void;
  // Lets the parent dialog mirror the resolved outcome in its own title
  // instead of always showing "pending" (Task 17 review, Minor #3).
  onStatusChange?: (status: TerminalStatus) => void;
  // Overrides the pending-state body (default: payment.pendingTitle, the
  // Stripe checkout dialog's neutral "processing" text). The Paynet-return
  // page (product/[id].tsx, Task 10) passes its own longer, honest copy
  // here instead: Stripe confirms in seconds, but a lost Paynet callback
  // can leave this pending for ~15 minutes (reconcilePaynetPayments —
  // staleAfterMs 10min + intervalMs 5min) before the periodic sweep picks
  // it up, and the generic "processing" text reads as stuck rather than
  // "this is expected, wait a bit longer" over that span.
  pendingContent?: ReactNode;
}

export const PaymentStatusStep: FC<PaymentStatusStepProps> = ({
  paymentId,
  onClose,
  onStatusChange,
  pendingContent,
}) => {
  const { t } = useTranslation();
  const { status, loading, error } = usePayment(paymentId);
  const isStuck = !loading && !status;
  const terminalStatus = resolveTerminalStatus(status, !!error || isStuck);

  useEffect(() => {
    if (terminalStatus) onStatusChange?.(terminalStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminalStatus]);

  if (status === "SUCCEEDED") {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6">{t("payment.succeededTitle")}</Typography>
        {/* Референс Payment.js:70-79: под заголовком статуса всегда идёт
            подзаголовок body2, а кнопка закрытия на всю ширину. */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("payment.succeededSubheader")}
        </Typography>
        <Button sx={{ mt: 2 }} fullWidth variant="contained" onClick={onClose}>
          {t("payment.closeButton")}
        </Button>
      </Box>
    );
  }

  // Отменённый и возвращённый платёж показываются одинаково: отдельного
  // текста про возврат в сиде переводов витрины нет (payment.* знает только
  // canceledTitle), выдумывать ключ здесь не стали.
  if (terminalStatus === "CANCELED" || terminalStatus === "REFUNDED") {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6">{t("payment.canceledTitle")}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("payment.canceledSubheader")}
        </Typography>
        <Button sx={{ mt: 2 }} fullWidth variant="contained" onClick={onClose}>
          {t("payment.closeButton")}
        </Button>
        {terminalStatus === "CANCELED" && (
          <Box sx={{ mt: 1 }}>
            <FixWithAssistantButton
              errorCode="PAYMENT_FAILED"
              paymentId={paymentId}
            />
          </Box>
        )}
      </Box>
    );
  }

  // A poll network error, or a poll that resolved without ever finding the
  // payment (payment: null), leaves nothing else to wait on — surface an
  // error instead of an indefinite spinner (Task 17 review, Finding 4).
  if (error || isStuck) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("payment.errorTitle")}
        </Alert>
        <Button variant="outlined" onClick={onClose}>
          {t("payment.closeButton")}
        </Button>
        <Box sx={{ mt: 1 }}>
          <FixWithAssistantButton
            errorCode="PAYMENT_FAILED"
            paymentId={paymentId}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <CircularProgress sx={{ mb: 2 }} />
      {pendingContent ?? (
        <>
          <Typography variant="body1">{t("payment.pendingTitle")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("payment.pendingSubheader")}
          </Typography>
        </>
      )}
    </Box>
  );
};
